// client/src/pages/SnowflakeCostReport.tsx
//
// Snowflake Cost — where our Snowflake credits go, and which of them buy nothing.
//
// Backed by api/snowflake-cost, which reads ADMIN_VIEWS in Snowflake directly.
// There is no local table and no collector: Snowflake retains 365 days itself,
// so copying it would add sync lag and failure modes to solve a retention
// problem that does not exist. The read layer caches for an hour instead,
// because every Snowflake read resumes a warehouse and costs credits.
//
// THE METRIC THAT MATTERS is idle credits — billed minus what Snowflake could
// attribute to an actual query. Total spend tells you the bill; the idle share
// tells you what the bill bought. Measured 2026-08-19, BMSS_CONCUR_WH was 12.6%
// attributed: 1,289 of 1,475 credits paid for a warehouse doing nothing.
//
// AND THE FIX DEPENDS ON A SECOND NUMBER, which is why the Auto-suspend tab
// exists. Idle looks recoverable until you check the gaps between queries. If
// they are all shorter than the suspend threshold, auto-suspend saves nothing
// and only changing what polls the warehouse will help. Reading idle alone and
// reaching for AUTO_SUSPEND is the wrong-conclusion trap this page is shaped to
// prevent.
//
// Chart notes (see the dataviz method):
//   * Categorical palette is #3987e5 / #d95926 / #1d9a8f, validated all-pairs
//     against the dark surface. NOTE it is not PE Health's set: that page's
//     violet #9085e9 sits ΔE 9.8 from its blue for normal vision, which passes
//     adjacent-only checking but fails all-pairs — and a stacked bar makes every
//     pair comparable. Do not copy that palette here.
//   * Attributed vs idle is a two-part composition of one total, so it is one
//     stacked bar with a 2px surface gap, not two bars.
//   * No dual axes anywhere. Credits and percentages never share a scale.
//   * Every chart has a table beneath it carrying the same numbers.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Snowflake, AlertTriangle, RefreshCw, Gauge, Database, Users,
  ListChecks, Timer, Activity, CheckCircle2, TriangleAlert,
} from 'lucide-react';

// ---------- palette ----------
// validate_palette.js "#3987e5,#d95926,#1d9a8f" --mode dark --surface #121417 --pairs all
//   all checks pass · worst all-pairs ΔE 14.1 deutan / 15.8 normal
//
// Two roles, kept deliberately separate:
//   USE/IDLE  — the composition encoding on the hero chart
//   WAREHOUSE — categorical identity in the daily trend
const USE = { attributed: '#3987e5', idle: '#d95926' };
const WAREHOUSE_HUES = ['#3987e5', '#d95926', '#1d9a8f'];
const OTHER_HUE = '#6b7280'; // neutral: "Other" is a bucket, not an identity
const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b' };
const GRID = 'rgba(255,255,255,.07)';

// Idle share thresholds. Above 50% something is structurally wrong; above 75%
// the warehouse is essentially being paid to exist.
const IDLE_WARN = 50;
const IDLE_CRIT = 75;

// ---------- types ----------
// `state` distinguishes the three ways a source can be unusable, which have
// three different owners: stale (the DBA task stopped), empty (it runs but
// populates nothing), missing (the object is gone — seen for real on 2026-08-20
// while QUERY_HISTORY was being rebuilt).
type SourceState = 'current' | 'stale' | 'empty' | 'missing';
type SourceRow = {
  source: string; newest: string | null; rows: number; ageHours: number | null;
  healthy: boolean; state: SourceState; error?: string;
};
type Status = { ok: boolean; sources: SourceRow[]; warehouseScope: string[]; cacheTtlMin: number; fromCache?: boolean; cacheAgeSec?: number };
type WarehouseRow = {
  WAREHOUSE: string; CREDITS_METERED: number; CREDITS_ATTRIBUTED: number; CREDITS_IDLE: number;
  PCT_ATTRIBUTED: number | null; BILLED_HOURS: number; ACTIVE_HOURS: number; QUERIES: number; USERS: number;
};
type DailyRow = { USAGE_DATE: string; SERVICE_TYPE: string; CREDITS: number; CREDITS_COMPUTE: number; CREDITS_CLOUD: number };
type HourlyRow = { HOUR_UTC: string; WAREHOUSE: string; CREDITS: number };
type SuspendRow = {
  WAREHOUSE: string; WINDOW_HOURS: number; IDLE_HOURS: number;
  RESUMES_AT_60S: number; SUSPENDABLE_HRS_60S: number;
  RESUMES_AT_300S: number; SUSPENDABLE_HRS_300S: number;
  RESUMES_AT_600S: number; SUSPENDABLE_HRS_600S: number;
};
type ShapeRow = {
  PARAM_HASH: string; WAREHOUSE: string; USER_NAME: string; QUERY_TAG: string | null;
  EXECS: number; CREDITS: number | null; ACTIVE_MINUTES: number; SNIPPET: string | null;
};
type PollerRow = {
  EXECS: number; DISTINCT_MINUTES: number; WINDOW_MINUTES: number;
  AVG_SEC: number; MAX_SEC: number; TOTAL_MIN: number; USER_NAME: string; SNIPPET: string;
};
type UserRow = { USER_NAME: string; WAREHOUSE: string; QUERIES: number; CREDITS: number; ACTIVE_HOURS: number };

type Tab = 'overview' | 'shapes' | 'pollers' | 'suspend' | 'users' | 'sources';

// ---------- helpers ----------
async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(url, { credentials: 'include', signal });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try { const b = await r.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return r.json();
}
const int = (v: number | null | undefined) => (v === null || v === undefined ? '—' : Math.round(v).toLocaleString());
const cr = (v: number | null | undefined, dp = 1) =>
  v === null || v === undefined ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);

const idleColor = (p: number | null | undefined) =>
  p === null || p === undefined ? 'var(--text)'
    : p >= IDLE_CRIT ? STATUS.critical : p >= IDLE_WARN ? STATUS.warning : STATUS.good;

// Snowflake returns "2026-08-19 11:00:00.000 -0700" — has an offset, so Date
// parses it correctly. Guard anyway; a bad parse silently shifts every reading.
const asDate = (s: string) => { const d = new Date(s); return Number.isNaN(d.getTime()) ? null : d; };
const shortDay = (s: string) => (s || '').slice(5, 10);
const shortHour = (s: string) => { const d = asDate(s); return d ? d.toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', hour12: true }) : s; };

// ---------- small UI bits ----------
function Kpi({ label, value, accent, icon, sub }: {
  label: string; value: React.ReactNode; accent?: string; icon?: React.ReactNode; sub?: string;
}) {
  return (
    <div className="card" style={{ padding: '12px 18px', minWidth: 150, textAlign: 'center', borderLeft: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        {icon}
        <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: accent || 'var(--text)' }}>{value}</span>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 5 }}>{label}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2, opacity: .75 }}>{sub}</div>}
    </div>
  );
}

// Identity is a swatch beside text-token text; the text itself never wears the
// series color.
function Swatch({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text)' }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flex: '0 0 auto' }} />
      {children}
    </span>
  );
}

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <Gauge size={14} /> },
  { key: 'shapes', label: 'Query shapes', icon: <ListChecks size={14} /> },
  { key: 'pollers', label: 'What holds it open', icon: <Timer size={14} /> },
  { key: 'suspend', label: 'Auto-suspend', icon: <Activity size={14} /> },
  { key: 'users', label: 'By user', icon: <Users size={14} /> },
  { key: 'sources', label: 'Data health', icon: <Database size={14} /> },
];

function TabBar({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  return (
    <div className="row" style={{ gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      {TABS.map((t) => (
        <button key={t.key} onClick={() => setTab(t.key)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600,
            padding: '6px 13px', borderRadius: 9, cursor: 'pointer', color: 'var(--text)',
            border: `1px solid ${tab === t.key ? USE.attributed : 'var(--border)'}`,
            background: tab === t.key ? 'color-mix(in srgb, #3987e5 16%, transparent)' : 'transparent',
          }}>
          {t.icon}{t.label}
        </button>
      ))}
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return <th style={{ textAlign: right ? 'right' : 'left', padding: '7px 10px', whiteSpace: 'nowrap' }}>{children}</th>;
}
function Td({ children, right, mono }: { children: React.ReactNode; right?: boolean; mono?: boolean }) {
  return (
    <td style={{
      textAlign: right ? 'right' : 'left', padding: '6px 10px',
      fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
      fontSize: mono ? 11.5 : undefined,
    }}>{children}</td>
  );
}

// ---------- page ----------
export default function SnowflakeCostReport() {
  const [tab, setTab] = useState<Tab>('overview');
  const [days, setDays] = useState(30);
  const [warehouse, setWarehouse] = useState<string>('BMSS_CONCUR_WH');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [status, setStatus] = useState<Status | null>(null);
  const [whRows, setWhRows] = useState<WarehouseRow[]>([]);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [hourly, setHourly] = useState<HourlyRow[]>([]);
  const [shapes, setShapes] = useState<ShapeRow[]>([]);
  const [shapeTextErr, setShapeTextErr] = useState<string | null>(null);
  const [pollers, setPollers] = useState<PollerRow[]>([]);
  const [pollerNote, setPollerNote] = useState<string>('');
  const [suspend, setSuspend] = useState<SuspendRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);

  // Core load: status + warehouses + daily. Everything else is per-tab so a
  // page visit does not pay for panels nobody opened — these reads cost credits.
  const loadCore = useCallback(async (signal?: AbortSignal, refresh = false) => {
    setBusy(true); setErr(null);
    const r = refresh ? '&refresh=1' : '';
    try {
      const [s, w, d] = await Promise.all([
        getJson<Status>(`/api/snowflake-cost?mode=status${r}`, signal),
        getJson<{ rows: WarehouseRow[] }>(`/api/snowflake-cost?mode=warehouses&days=${days}${r}`, signal),
        getJson<{ rows: DailyRow[] }>(`/api/snowflake-cost?mode=daily&days=60${r}`, signal),
      ]);
      setStatus(s); setWhRows(w.rows || []); setDaily(d.rows || []);
    } catch (e: any) {
      if (e?.name !== 'AbortError') setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [days]);

  useEffect(() => {
    const c = new AbortController();
    loadCore(c.signal);
    return () => c.abort();
  }, [loadCore]);

  // Per-tab lazy loads.
  useEffect(() => {
    const c = new AbortController();
    (async () => {
      try {
        if (tab === 'overview' && !hourly.length) {
          const h = await getJson<{ rows: HourlyRow[] }>('/api/snowflake-cost?mode=hourly&days=7', c.signal);
          setHourly(h.rows || []);
        } else if (tab === 'shapes') {
          const s = await getJson<{ rows: ShapeRow[]; textUnavailable?: string }>(
            `/api/snowflake-cost?mode=shapes&days=${days}&limit=40`, c.signal);
          setShapes(s.rows || []);
          setShapeTextErr(s.textUnavailable || null);
        } else if (tab === 'pollers') {
          const p = await getJson<{ rows: PollerRow[]; note: string }>(
            `/api/snowflake-cost?mode=pollers&hours=3&warehouse=${encodeURIComponent(warehouse)}`, c.signal);
          setPollers(p.rows || []); setPollerNote(p.note || '');
        } else if (tab === 'suspend' && !suspend.length) {
          const s = await getJson<{ rows: SuspendRow[] }>('/api/snowflake-cost?mode=suspend&days=7', c.signal);
          setSuspend(s.rows || []);
        } else if (tab === 'users' && !users.length) {
          const u = await getJson<{ rows: UserRow[] }>(`/api/snowflake-cost?mode=users&days=${days}`, c.signal);
          setUsers(u.rows || []);
        }
      } catch (e: any) {
        if (e?.name !== 'AbortError') setErr(e.message || String(e));
      }
    })();
    return () => c.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, days, warehouse]);

  // ---------- derived ----------
  const totals = useMemo(() => {
    const metered = whRows.reduce((a, r) => a + (r.CREDITS_METERED || 0), 0);
    const idle = whRows.reduce((a, r) => a + (r.CREDITS_IDLE || 0), 0);
    return { metered, idle, idlePct: metered ? (idle / metered) * 100 : null };
  }, [whRows]);

  const worst = useMemo(
    () => whRows.slice().sort((a, b) => (b.CREDITS_IDLE || 0) - (a.CREDITS_IDLE || 0))[0] || null,
    [whRows]
  );

  // A warehouse busy in nearly every billed hour cannot be fixed by auto-suspend
  // — that is a polling problem. Surfacing the count keeps the distinction in
  // front of the reader rather than in a footnote.
  const alwaysOn = useMemo(
    () => whRows.filter((r) => r.BILLED_HOURS > 0 && r.ACTIVE_HOURS / r.BILLED_HOURS >= 0.98).length,
    [whRows]
  );

  const feedProblems = useMemo(
    () => (status?.sources || []).filter((s) => !s.healthy),
    [status]
  );

  // Daily totals across every service type — one series, so no legend and no
  // categorical color needed. Warehouse identity lives on the hero chart.
  const dailyTotals = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of daily) m.set(d.USAGE_DATE, (m.get(d.USAGE_DATE) || 0) + (d.CREDITS || 0));
    return Array.from(m.entries()).map(([date, credits]) => ({ date, credits })).sort((a, b) => a.date.localeCompare(b.date));
  }, [daily]);

  // Hourly, pivoted to top-3 warehouses by spend + Other.
  const { hourlySeries, hourlyKeys } = useMemo(() => {
    const top = whRows.slice().sort((a, b) => b.CREDITS_METERED - a.CREDITS_METERED).slice(0, 3).map((r) => r.WAREHOUSE);
    const keys = [...top, 'Other'];
    const m = new Map<string, any>();
    for (const h of hourly) {
      const row = m.get(h.HOUR_UTC) || { hour: h.HOUR_UTC };
      const k = top.includes(h.WAREHOUSE) ? h.WAREHOUSE : 'Other';
      row[k] = (row[k] || 0) + (h.CREDITS || 0);
      m.set(h.HOUR_UTC, row);
    }
    return {
      hourlySeries: Array.from(m.values()).sort((a, b) => String(a.hour).localeCompare(String(b.hour))),
      hourlyKeys: keys,
    };
  }, [hourly, whRows]);

  const hueFor = (key: string, i: number) => (key === 'Other' ? OTHER_HUE : WAREHOUSE_HUES[i % WAREHOUSE_HUES.length]);

  const refresh = () => { const c = new AbortController(); loadCore(c.signal, true); };

  return (
    <div style={{ padding: '18px 22px 60px' }}>
      {/* ---------- header ---------- */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 9, margin: 0, fontSize: 22 }}>
            <Snowflake size={20} /> Snowflake Cost
          </h1>
          <p className="muted" style={{ maxWidth: 940, fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
            Where our Snowflake credits go. The number that matters is <strong>idle</strong> — credits
            billed for a warehouse that was running but executing nothing. Total spend is the bill;
            the idle share is what the bill bought. Figures cover warehouses matching{' '}
            <strong>{(status?.warehouseScope || ['BMSS_']).join(', ')}</strong>; the Snowflake org is
            shared with other firms and their warehouses are excluded.
          </p>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <label className="muted" style={{ fontSize: 12 }}>Window</label>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
            style={{ padding: '4px 8px', borderRadius: 8, background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)' }}>
            <option value={7}>7d</option><option value={30}>30d</option><option value={90}>90d</option>
          </select>
          <button onClick={refresh} disabled={busy}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600,
              padding: '6px 12px', borderRadius: 9, cursor: busy ? 'default' : 'pointer',
              border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', opacity: busy ? .6 : 1,
            }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="card" style={{ padding: 12, margin: '14px 0', borderLeft: `3px solid ${STATUS.critical}`, fontSize: 13 }}>
          <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{err}
        </div>
      )}

      {/* A stale source and a quiet account look identical on a chart, so say it
          in the banner rather than leaving it to the Data health tab. */}
      {feedProblems.length > 0 && (
        <div className="card" style={{ padding: 12, margin: '14px 0', borderLeft: `3px solid ${STATUS.warning}`, fontSize: 13 }}>
          <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          {feedProblems.length} source {feedProblems.length === 1 ? 'table is' : 'tables are'} stale or empty
          ({feedProblems.map((f) => f.source).join(', ')}). Anything drawn from{' '}
          {feedProblems.length === 1 ? 'it' : 'them'} is incomplete — see <strong>Data health</strong>.
        </div>
      )}

      {/* ---------- KPI row ---------- */}
      <div className="row" style={{ gap: 10, margin: '16px 0' }}>
        <Kpi label={`Credits billed (${days}d)`} value={cr(totals.metered, 0)} icon={<Snowflake size={16} />}
          sub={`${whRows.length} warehouse${whRows.length === 1 ? '' : 's'}`} />
        <Kpi label="Idle credits" value={cr(totals.idle, 0)} accent={idleColor(totals.idlePct)}
          sub={totals.idlePct !== null ? `${pct(totals.idlePct)} of the bill` : undefined} />
        <Kpi label="Biggest idle source" value={worst ? worst.WAREHOUSE.replace(/^BMSS_/, '') : '—'}
          accent={worst ? idleColor(100 - (worst.PCT_ATTRIBUTED ?? 0)) : undefined}
          sub={worst ? `${cr(worst.CREDITS_IDLE, 0)} credits idle` : undefined} />
        <Kpi label="Never suspend" value={alwaysOn} accent={alwaysOn ? STATUS.warning : STATUS.good}
          icon={alwaysOn ? <TriangleAlert size={16} /> : <CheckCircle2 size={16} />}
          sub={alwaysOn ? 'busy in ~every billed hour' : 'all have idle windows'} />
        <Kpi label="Source data" value={feedProblems.length ? `${feedProblems.length} stale` : 'healthy'}
          accent={feedProblems.length ? STATUS.warning : STATUS.good} icon={<Database size={16} />}
          sub={status ? `cached ${status.cacheTtlMin}m` : undefined} />
      </div>

      <TabBar tab={tab} setTab={setTab} />

      {/* ================= OVERVIEW ================= */}
      {tab === 'overview' && (
        <>
          {/* Hero: attributed vs idle per warehouse. One stacked bar per
              warehouse because the two parts sum to the bill for that warehouse. */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
              Credits by warehouse — last {days} days
              <span className="muted" style={{ fontWeight: 400 }}> · each bar is that warehouse&rsquo;s bill, split by whether a query used it</span>
            </div>
            <div style={{ padding: '10px 14px 4px' }}>
              <ResponsiveContainer width="100%" height={Math.max(180, whRows.length * 52 + 60)}>
                <BarChart data={whRows} layout="vertical" margin={{ top: 4, right: 28, bottom: 4, left: 8 }}>
                  <CartesianGrid stroke={GRID} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="var(--muted)"
                    label={{ value: 'credits', position: 'insideBottomRight', offset: -2, fontSize: 11, fill: 'var(--muted)' }} />
                  <YAxis type="category" dataKey="WAREHOUSE" width={150} tick={{ fontSize: 11.5 }} stroke="var(--muted)" />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: any, n: any) => [`${cr(v)} credits`, n]} />
                  <Legend wrapperStyle={{ fontSize: 11.5 }} />
                  {/* 2px surface gap between the segments, per the mark spec. */}
                  <Bar dataKey="CREDITS_ATTRIBUTED" name="Used by queries" stackId="c"
                    fill={USE.attributed} stroke="var(--card)" strokeWidth={2} />
                  <Bar dataKey="CREDITS_IDLE" name="Idle" stackId="c"
                    fill={USE.idle} stroke="var(--card)" strokeWidth={2} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead className="muted">
                  <tr>
                    <Th>Warehouse</Th><Th right>Billed</Th><Th right>Used</Th><Th right>Idle</Th>
                    <Th right>Used %</Th><Th right>Active hrs</Th><Th right>Billed hrs</Th><Th right>Queries</Th>
                  </tr>
                </thead>
                <tbody>
                  {whRows.map((r) => {
                    const alwaysBusy = r.BILLED_HOURS > 0 && r.ACTIVE_HOURS / r.BILLED_HOURS >= 0.98;
                    return (
                      <tr key={r.WAREHOUSE} style={{ borderTop: '1px solid var(--border)' }}>
                        <Td><Swatch color={USE.attributed}>{r.WAREHOUSE}</Swatch></Td>
                        <Td right>{cr(r.CREDITS_METERED)}</Td>
                        <Td right>{cr(r.CREDITS_ATTRIBUTED)}</Td>
                        <Td right><strong style={{ color: idleColor(100 - (r.PCT_ATTRIBUTED ?? 0)) }}>{cr(r.CREDITS_IDLE)}</strong></Td>
                        <Td right>{pct(r.PCT_ATTRIBUTED)}</Td>
                        <Td right>
                          {int(r.ACTIVE_HOURS)}
                          {alwaysBusy && (
                            <span title="Busy in ~every billed hour — auto-suspend cannot help; the schedule has to change."
                              style={{ marginLeft: 6, color: STATUS.warning }}>always on</span>
                          )}
                        </Td>
                        <Td right>{int(r.BILLED_HOURS)}</Td>
                        <Td right>{int(r.QUERIES)}</Td>
                      </tr>
                    );
                  })}
                  {!whRows.length && <tr><Td>{busy ? 'Loading…' : 'No warehouse data in this window.'}</Td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily trend — a single series, so no legend and no categorical hue. */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
              Account credits per day — last 60 days
              <span className="muted" style={{ fontWeight: 400 }}> · every service type, including storage and cloud services</span>
            </div>
            <div style={{ padding: '10px 14px 14px' }}>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={dailyTotals} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                  <defs>
                    <linearGradient id="sfDaily" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={USE.attributed} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={USE.attributed} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11 }} stroke="var(--muted)" minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} stroke="var(--muted)" width={48} />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: any) => [`${cr(v)} credits`, 'Credits']} />
                  <Area type="monotone" dataKey="credits" stroke={USE.attributed} strokeWidth={2}
                    fill="url(#sfDaily)" dot={false} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Hourly, stacked by warehouse. This is the series the Logic App run
              overlay will line up against once wired. */}
          <div className="card">
            <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
              Credits per hour by warehouse — last 7 days
              <span className="muted" style={{ fontWeight: 400 }}> · top 3 by spend; everything else grouped as Other</span>
            </div>
            <div style={{ padding: '10px 14px 14px' }}>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={hourlySeries} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="hour" tickFormatter={shortHour} tick={{ fontSize: 11 }} stroke="var(--muted)" minTickGap={40} />
                  <YAxis tick={{ fontSize: 11 }} stroke="var(--muted)" width={48} />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    labelFormatter={(l: any) => shortHour(String(l))}
                    formatter={(v: any, n: any) => [`${cr(v, 2)} credits`, n]} />
                  <Legend wrapperStyle={{ fontSize: 11.5 }} />
                  {hourlyKeys.map((k, i) => (
                    <Area key={k} type="monotone" dataKey={k} name={k} stackId="w"
                      stroke={hueFor(k, i)} strokeWidth={2} fill={hueFor(k, i)} fillOpacity={0.28} dot={false} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* ================= QUERY SHAPES ================= */}
      {tab === 'shapes' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            Query shapes by credits — last {days} days
            <div className="muted" style={{ fontWeight: 400, fontSize: 11.5, marginTop: 3, maxWidth: 900 }}>
              Grouped by Snowflake&rsquo;s parameterised hash, so the same statement with different
              literals is one row. Attribution to a user is a <strong>measurement</strong> here, not a
              guess — Snowflake stamps it on every query. Rows reading &ldquo;no text recovered&rdquo;
              are real and costed; their text simply post-dates the last refresh of the source table.
            </div>
            {shapeTextErr && (
              <div style={{ fontWeight: 400, fontSize: 11.5, marginTop: 8, color: STATUS.warning, maxWidth: 900 }}>
                <TriangleAlert size={12} style={{ verticalAlign: -2, marginRight: 5 }} />
                Statement text is unavailable right now — the <code>QUERY_HISTORY</code> source could
                not be read, so every row below shows costs without SQL. The credits are unaffected.
                See <strong>Data health</strong>.
              </div>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead className="muted">
                <tr>
                  <Th right>Credits</Th><Th right>Execs</Th><Th right>Active min</Th>
                  <Th>User</Th><Th>Warehouse</Th><Th>Statement</Th>
                </tr>
              </thead>
              <tbody>
                {shapes.map((s) => (
                  <tr key={s.PARAM_HASH} style={{ borderTop: '1px solid var(--border)' }}>
                    <Td right><strong>{cr(s.CREDITS, 2)}</strong></Td>
                    <Td right>{int(s.EXECS)}</Td>
                    <Td right>{int(s.ACTIVE_MINUTES)}</Td>
                    <Td>{s.USER_NAME}</Td>
                    <Td>{s.WAREHOUSE}</Td>
                    <Td mono>
                      {s.SNIPPET
                        ? s.SNIPPET.slice(0, 180)
                        : <span className="muted">no text recovered — shape first seen after the source table&rsquo;s last refresh</span>}
                    </Td>
                  </tr>
                ))}
                {!shapes.length && <tr><Td>{busy ? 'Loading…' : 'No query shapes in this window.'}</Td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================= POLLERS ================= */}
      {tab === 'pollers' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            What holds {warehouse} open — last 3 hours
            <div style={{ marginTop: 8 }}>
              <select value={warehouse} onChange={(e) => setWarehouse(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 8, background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 12 }}>
                {(whRows.length ? whRows.map((w) => w.WAREHOUSE) : ['BMSS_CONCUR_WH']).map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </div>
            <div className="muted" style={{ fontWeight: 400, fontSize: 11.5, marginTop: 6, maxWidth: 900 }}>
              Ranked by <strong>distinct minutes touched</strong>, not by cost. A sub-second query firing
              every few seconds attributes almost nothing to itself yet is exactly what stops a warehouse
              ever suspending — ranking this tab by credits would bury the cause under its symptoms.
              {pollerNote ? ` ${pollerNote}` : ''}
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead className="muted">
                <tr>
                  <Th right>Minutes touched</Th><Th right>Execs</Th><Th right>Avg</Th>
                  <Th right>Max</Th><Th right>Total</Th><Th>User</Th><Th>Statement</Th>
                </tr>
              </thead>
              <tbody>
                {pollers.map((p, i) => {
                  const coverage = p.WINDOW_MINUTES ? (p.DISTINCT_MINUTES / p.WINDOW_MINUTES) * 100 : 0;
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <Td right>
                        <strong style={{ color: coverage >= 80 ? STATUS.critical : coverage >= 40 ? STATUS.warning : 'var(--text)' }}>
                          {int(p.DISTINCT_MINUTES)}
                        </strong>
                        <span className="muted"> / {int(p.WINDOW_MINUTES)}</span>
                      </Td>
                      <Td right>{int(p.EXECS)}</Td>
                      <Td right>{cr(p.AVG_SEC, 2)}s</Td>
                      <Td right>{cr(p.MAX_SEC, 2)}s</Td>
                      <Td right>{cr(p.TOTAL_MIN)}m</Td>
                      <Td>{p.USER_NAME}</Td>
                      <Td mono>{(p.SNIPPET || '').slice(0, 180)}</Td>
                    </tr>
                  );
                })}
                {!pollers.length && <tr><Td>{busy ? 'Loading…' : 'No queries on this warehouse in the last 3 hours.'}</Td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================= AUTO-SUSPEND ================= */}
      {tab === 'suspend' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            What auto-suspend could actually recover — last 7 days
            <div className="muted" style={{ fontWeight: 400, fontSize: 11.5, marginTop: 3, maxWidth: 920 }}>
              Derived from the gaps between consecutive queries, so it is an <strong>estimate</strong>,
              not a measurement. Read each pair together: Snowflake bills a 60-second minimum on every
              resume, so hours saved at a threshold are only worth having if the resume count beside them
              is tolerable. A warehouse showing <strong>0 recoverable hours at 300s</strong> has no gap
              longer than five minutes anywhere in the week — something polls it constantly, and no
              auto-suspend setting will help until that changes.
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead className="muted">
                <tr>
                  <Th>Warehouse</Th><Th right>Idle hrs</Th>
                  <Th right>Save @60s</Th><Th right>Resumes</Th>
                  <Th right>Save @300s</Th><Th right>Resumes</Th>
                  <Th right>Save @600s</Th><Th right>Resumes</Th>
                </tr>
              </thead>
              <tbody>
                {suspend.map((s) => (
                  <tr key={s.WAREHOUSE} style={{ borderTop: '1px solid var(--border)' }}>
                    <Td>{s.WAREHOUSE}</Td>
                    <Td right>{cr(s.IDLE_HOURS)} <span className="muted">/ {int(s.WINDOW_HOURS)}</span></Td>
                    <Td right>{cr(s.SUSPENDABLE_HRS_60S)}</Td>
                    <Td right>{int(s.RESUMES_AT_60S)}</Td>
                    <Td right>
                      <strong style={{ color: s.SUSPENDABLE_HRS_300S > 0 ? STATUS.good : STATUS.warning }}>
                        {cr(s.SUSPENDABLE_HRS_300S)}
                      </strong>
                    </Td>
                    <Td right>{int(s.RESUMES_AT_300S)}</Td>
                    <Td right>{cr(s.SUSPENDABLE_HRS_600S)}</Td>
                    <Td right>{int(s.RESUMES_AT_600S)}</Td>
                  </tr>
                ))}
                {!suspend.length && <tr><Td>{busy ? 'Loading…' : 'No gap data in this window.'}</Td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================= USERS ================= */}
      {tab === 'users' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            Credits by user — last {days} days
            <div className="muted" style={{ fontWeight: 400, fontSize: 11.5, marginTop: 3, maxWidth: 900 }}>
              Snowflake stamps <code>USER_NAME</code> on every query, so this is measured rather than
              inferred. A named person&rsquo;s account dominating around the clock is not a busy human —
              it is the automation estate running on a personal credential.
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead className="muted">
                <tr><Th>User</Th><Th>Warehouse</Th><Th right>Credits</Th><Th right>Queries</Th><Th right>Active hrs</Th></tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={`${u.USER_NAME}-${u.WAREHOUSE}-${i}`} style={{ borderTop: '1px solid var(--border)' }}>
                    <Td>{u.USER_NAME}</Td>
                    <Td>{u.WAREHOUSE}</Td>
                    <Td right><strong>{cr(u.CREDITS, 2)}</strong></Td>
                    <Td right>{int(u.QUERIES)}</Td>
                    <Td right>{int(u.ACTIVE_HOURS)}</Td>
                  </tr>
                ))}
                {!users.length && <tr><Td>{busy ? 'Loading…' : 'No user data in this window.'}</Td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================= DATA HEALTH ================= */}
      {tab === 'sources' && (
        <div className="card">
          <div className="hd" style={{ fontSize: 13, fontWeight: 600 }}>
            Source freshness
            <div className="muted" style={{ fontWeight: 400, fontSize: 11.5, marginTop: 3, maxWidth: 920 }}>
              We cannot read <code>SNOWFLAKE.ACCOUNT_USAGE</code> directly — the org is administered by
              a third party and contains other firms&rsquo; data. A DBA-owned task materialises these
              tables into <code>ADMIN_VIEWS</code> every 6 hours instead, so freshness is that cadence
              plus Snowflake&rsquo;s own 45-minute-to-6-hour lag. Anything under a day old is healthy.
              A table that stops refreshing looks exactly like a quiet account on every other tab, which
              is why this one exists.
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead className="muted">
                <tr><Th>Source table</Th><Th>Newest row</Th><Th right>Age</Th><Th right>Rows</Th><Th>State</Th></tr>
              </thead>
              <tbody>
                {(status?.sources || []).map((s) => (
                  <tr key={s.source} style={{ borderTop: '1px solid var(--border)' }}>
                    <Td mono>{s.source}</Td>
                    <Td>{s.newest || <span className="muted">never</span>}</Td>
                    <Td right>{s.ageHours === null ? '—' : `${int(s.ageHours)}h`}</Td>
                    <Td right>{int(s.rows)}</Td>
                    <Td>
                      {s.state === 'current' ? (
                        <span style={{ color: STATUS.good }}>
                          <CheckCircle2 size={13} style={{ verticalAlign: -2, marginRight: 5 }} />current
                        </span>
                      ) : (
                        <span
                          title={s.error || undefined}
                          style={{ color: s.state === 'missing' ? STATUS.critical : STATUS.warning }}>
                          <TriangleAlert size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
                          {s.state}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
                {!status && <tr><Td>{busy ? 'Loading…' : 'No status available.'}</Td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
